// Library methods
import { useMemo } from "react";

// Components
import Card from "@mui/material/Card";
import { useTheme } from "@mui/material/styles";
import DeviceCardHeader from "./DeviceCardHeader/DeviceCardHeader";
import DeviceCardContent from "./DeviceCardContent/DeviceCardContent";
import DeviceCardButton from "./DeviceCardButton/DeviceCardButton";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { DeviceType } from "src/services/devices/types";

type DeviceCardType = {
  device: DeviceType;
};

const DeviceCard = ({ device }: DeviceCardType) => {
  const theme = useTheme();
  const { pageWash, surface, onSurface } = getSurfaceTokens(theme);

  const isLinked = useMemo(
    () => Boolean(device?.associatedExam),
    [device?.associatedExam]
  );

  const isOutdated = useMemo(
    () => !device?.webserviceCompatible,
    [device?.webserviceCompatible]
  );

  const shouldDisableLiveUpdate = useMemo(
    () =>
      !device?.id ||
      !device?.associatedExam?.exam ||
      !device?.deviceStatus?.stage?.stageType,
    [
      device?.id,
      device?.associatedExam?.exam,
      device?.deviceStatus?.stage?.stageType,
    ]
  );

  // Same teal DeviceCardButton uses for its dark-mode "Unlink" background --
  // keeps the linked state's accent consistent across the whole card.
  const linkedBorderColor =
    theme.palette.mode === "dark" ? "#3B868E" : onSurface;

  return (
    <Card
      elevation={0}
      key={device?.id}
      sx={{
        position: "relative",
        borderRadius: "8px",
        backgroundColor: theme.palette.mode === "dark" ? pageWash : surface,
        border: isLinked
          ? `2px solid ${linkedBorderColor}`
          : isOutdated
            ? "2px solid #87201A"
            : "2px solid #E2772E",
        p: "12px",
        minWidth: "280px",
        maxWidth: "280px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "8px",
        "@keyframes color-change-border": {
          "0%": {
            border: `2px solid ${linkedBorderColor}`,
          },
          "50%": {
            border: "2px solid #22BE39",
          },
          "100%": {
            border: `2px solid ${linkedBorderColor}`,
          },
        },
        animation:
          shouldDisableLiveUpdate || isOutdated || !isLinked
            ? "none"
            : "color-change-border 2s infinite",
      }}
    >
      {/* Card Header with Device Name and Live Update Icon */}
      <DeviceCardHeader
        device={device}
        isLinked={isLinked}
        isOutdated={isOutdated}
        shouldDisableLiveUpdate={shouldDisableLiveUpdate}
      />

      {/* Controller Icons and Patient Info */}
      {/* Patient Name and Device Status */}
      {/* Device expiry text and Online/Offline status */}
      <DeviceCardContent
        device={device}
        isLinked={isLinked}
        shouldDisableLiveUpdate={shouldDisableLiveUpdate}
      />

      {/* Link/Unlink/Outdated button */}
      <DeviceCardButton
        device={device}
        isLinked={isLinked}
        isOutdated={isOutdated}
      />
    </Card>
  );
};

export default DeviceCard;
